import { Dispatch } from "redux";

import {
  HIGH_FREQUENCY_WORD_IMPORT,
  HIGH_FREQUENCY_INIT_SPEC,
  HIGH_FREQUENCY_SELECT_TRUCK,
  HIGH_FREQUENCY_SELECT_TRUCK_TYPE,
  HIGH_FREQUENCY_SELECT_PLACE,
  HIGH_FREQUENCY_START_GAME,
  HIGH_FREQUENCY_SET_NEW_ROUND,
  HIGH_FREQUENCY_FINISH,
  HIGH_FREQUENCY_DRAG,
  HIGH_FREQUENCY_CHECK_ANSWER,
  HIGH_FREQUENCY_CANCEL_RESPONSE,
  HIGH_FREQUENCY_RESET_DISPLAY,
  HIGH_FREQUENCY_PLAY_AGAIN,
  HIGH_FREQUENCY_TRUCK_ACTIVE,
  HIGH_FREQUENCY_TRUCK_LEFT,
  HIGH_FREQUENCY_TRUCK_TRANS,
  HIGH_FREQUENCY_FIRST_CLICK,
} from "./actionIdentifiers";
import { findJsonValue, timeout } from "../../util/Util";
import { State } from "../store";
import { get, srcPath } from "../../util/Path";
import { gameConfigLoad } from "./shared";

export const TRUCK_SPEED = 3200;

export const soundAudio = {
  correct: new Audio(),
  wrong: new Audio(),
  engineIn: new Audio(),
  engineOut: new Audio(),
};

export const firstClick = () => {
  return { type: HIGH_FREQUENCY_FIRST_CLICK };
};

export const importSpec = (grade: number, unit: number) => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    const res = await get("config/high-frequency-word.json");
    const highFrequencyWordList = res.data;

    const truckOptions = findJsonValue(
      highFrequencyWordList,
      "trucks",
      { grade, unit },
      true
    );
    const placeOptions = findJsonValue(
      highFrequencyWordList,
      "places",
      { grade, unit },
      true
    );
    const totalRound = findJsonValue(highFrequencyWordList, "totalRound", {
      grade,
      unit,
    });
    const words = findJsonValue(
      highFrequencyWordList,
      "words",
      { grade, unit },
      true
    );
    const fileUnit = findJsonValue(highFrequencyWordList, "file_unit", {
      grade,
      unit,
    });

    const tempWords = [...words];
    tempWords.sort(() => Math.random() - 0.5);
    const answers = tempWords.slice(0, totalRound);

    dispatch({
      type: HIGH_FREQUENCY_WORD_IMPORT,
      payload: {
        truckOptions,
        placeOptions,
        totalRound,
        words,
        answers,
        fileUnit,
      },
    });

    dispatch(gameConfigLoad());
  };
};

export const initSpec = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { truckOptions, placeOptions } = getState().highFrequencyWord;

    let truckType: number, truck: string, place: number;

    if (truckOptions.length > 0) {
      truckType = truckOptions[0].truckType;

      const trucks = truckOptions[0].trucks;
      if (trucks.length > 0) truck = trucks[0];
    }

    if (placeOptions.length > 0) place = placeOptions[0];

    dispatch({
      type: HIGH_FREQUENCY_INIT_SPEC,
      payload: { truckType, truck, place },
    });
  };
};

export const selectTruck = (truck: string) => {
  return { type: HIGH_FREQUENCY_SELECT_TRUCK, payload: { truck } };
};

export const selectTruckType = (forward: boolean) => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { truckOptions, truckType } = getState().highFrequencyWord;
    const index = truckOptions.findIndex((t) => t.truckType === truckType);

    let newIndex: number;

    if (forward) {
      newIndex = index === truckOptions.length - 1 ? 0 : index + 1;
    } else {
      newIndex = index === 0 ? truckOptions.length - 1 : index - 1;
    }

    const newTruckObj = truckOptions[newIndex];
    const newTruckType = newTruckObj.truckType;

    let newTruck: string;
    if (newTruckObj.trucks.length > 0) {
      newTruck = newTruckObj.trucks[0];
    }

    dispatch({
      type: HIGH_FREQUENCY_SELECT_TRUCK_TYPE,
      payload: { truckType: newTruckType, truck: newTruck },
    });
  };
};

export const selectPlace = (place: number) => {
  return { type: HIGH_FREQUENCY_SELECT_PLACE, payload: { place } };
};

export const startGame = () => {
  return { type: HIGH_FREQUENCY_START_GAME };
};

export const setNewRound = () => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    dispatch({ type: HIGH_FREQUENCY_TRUCK_LEFT, payload: { truckLeft: "0%" } });

    const { round, totalRound } = getState().highFrequencyWord;
    const newRound = round + 1;

    if (newRound <= totalRound) {
      dispatch(setNewRoundInfo());

      if (!soundAudio.engineIn.src) {
        soundAudio.engineIn.src = srcPath("high-frequency-word/engine_on.wav");
      }
      soundAudio.engineIn.play();

      await timeout(300);
      dispatch({
        type: HIGH_FREQUENCY_TRUCK_TRANS,
        payload: { truckTransition: true },
      });
      await timeout(300);
      dispatch({
        type: HIGH_FREQUENCY_TRUCK_LEFT,
        payload: { truckLeft: "50%" },
      });
      await timeout(TRUCK_SPEED);
      dispatch({
        type: HIGH_FREQUENCY_TRUCK_ACTIVE,
        payload: { truckActive: true },
      });
    } else {
      dispatch(finish());
    }
  };
};

export const setNewRoundInfo = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { round, answers, words } = getState().highFrequencyWord;
    const newRound = round + 1;

    const answer = answers[newRound - 1];

    const tempWords = words.filter((w) => w !== answer);
    tempWords.sort(() => Math.random() - 0.5);
    const display = tempWords.slice(0, 2);
    display.push(answer);
    display.sort(() => Math.random() - 0.5);

    dispatch({
      type: HIGH_FREQUENCY_SET_NEW_ROUND,
      payload: {
        round: newRound,
        answer,
        display,
        answered: null,
      },
    });
  };
};

export const dragCard = (word: string, index: number) => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const display = [...getState().highFrequencyWord.display];
    display[index] = null;

    const answered = word;

    dispatch({
      type: HIGH_FREQUENCY_DRAG,
      payload: {
        answered,
        display,
      },
    });
  };
};

export const checkAnswer = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { answer, answered } = getState().highFrequencyWord;

    const response = answered === answer ? "correct" : "wrong";
    if (!soundAudio[response].src) {
      if (response === "correct") {
        soundAudio[response].src = srcPath(`shared/clapping.mp3`);
      } else {
        soundAudio[response].src = srcPath(
          `high-frequency-word/${response}.wav`
        );
      }
    }
    soundAudio[response].play();

    dispatch({ type: HIGH_FREQUENCY_CHECK_ANSWER, payload: { response } });

    soundAudio[response].onended = async () => {
      dispatch({ type: HIGH_FREQUENCY_CANCEL_RESPONSE });

      if (response === "correct") {
        if (!soundAudio.engineOut.src) {
          soundAudio.engineOut.src = srcPath(
            "high-frequency-word/engine_out.wav"
          );
        }
        soundAudio.engineOut.play();

        dispatch({
          type: HIGH_FREQUENCY_TRUCK_ACTIVE,
          payload: {
            truckActive: false,
          },
        });
        await timeout(300);
        dispatch({
          type: HIGH_FREQUENCY_TRUCK_LEFT,
          payload: {
            truckLeft: "100%",
          },
        });
        await timeout(TRUCK_SPEED);
        dispatch({
          type: HIGH_FREQUENCY_TRUCK_TRANS,
          payload: {
            truckTransition: false,
          },
        });
        await timeout(300);
        dispatch(setNewRound());
      } else {
        dispatch(resetDisplay());
      }
    };
  };
};

export const resetDisplay = () => {
  return (dispatch: Dispatch<any>, getState: () => State) => {
    const { answered, display } = getState().highFrequencyWord;
    const newDisplay = [...display];

    const index = newDisplay.indexOf(null);
    newDisplay[index] = answered;

    dispatch({
      type: HIGH_FREQUENCY_RESET_DISPLAY,
      payload: { display: newDisplay, answered: null },
    });
  };
};

export const finish = () => {
  return { type: HIGH_FREQUENCY_FINISH };
};

export const playAgain = () => {
  return async (dispatch: Dispatch<any>, getState: () => State) => {
    const { grade, unit } = getState().shared;

    dispatch({ type: HIGH_FREQUENCY_PLAY_AGAIN });
    await dispatch(importSpec(grade, unit));
    dispatch(initSpec());
  };
};
